import { useRef, useState } from "react";
import { Box, Flex, Text, VStack, Image, IconButton } from "@chakra-ui/react";
import { FiX } from "react-icons/fi";

interface FileUploadProps {
    selectedFiles: File[];
    onFilesChange: (files: File[]) => void;
    maxSize?: number; // em MB
    acceptedFormats?: string[];
    multiple?: boolean;
}

export function FileUpload({
    selectedFiles,
    onFilesChange,
    maxSize = 5,
    acceptedFormats = [".csv", ".xlsx"],
    multiple = true,
}: FileUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [fileErrors, setFileErrors] = useState<{ [filename: string]: string }>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFiles = (fileList: FileList | null) => {
        if (!fileList) return [];
        const errors: { [filename: string]: string } = {};
        const validFiles = Array.from(fileList).filter((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            const isValidExtension = acceptedFormats.some(
                (format) => format.toLowerCase().replace('.', '') === extension
            );
            const isValidSize = file.size <= maxSize * 1024 * 1024;

            if (!isValidExtension) {
                errors[file.name] = "Formato inválido";
                return false;
            }
            if (!isValidSize) {
                errors[file.name] = `Arquivo excede o tamanho permitido de ${maxSize} MB`;
                return false;
            }
            return true;
        });
        setFileErrors(errors);
        return validFiles;
    };

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;
        const validFiles = validateFiles(files);
        if (multiple) {
            const newFiles = validFiles.filter(
                (newFile) => !selectedFiles.some(
                    (existingFile) => existingFile.name === newFile.name && existingFile.size === newFile.size
                )
            );
            onFilesChange([...selectedFiles, ...newFiles]);
        } else {
            onFilesChange(validFiles);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        handleFileSelect(e.dataTransfer.files);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (index: number) => {
        const fileToRemove = selectedFiles[index];
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        onFilesChange(newFiles);
        if (fileToRemove && fileErrors[fileToRemove.name]) {
            const newErrors = { ...fileErrors };
            delete newErrors[fileToRemove.name];
            setFileErrors(newErrors);
        }
    };

    const formatAcceptedFormats = () => {
        return acceptedFormats.join("/").replace(/\./g, "");
    };

    return (
        <Flex flexDir="column" gap={2} w="100%">
            <Box
                border="2px dashed"
                borderColor={isDragging ? "#CB2159" : "#E6E9F0"}
                borderRadius="8px"
                p={8}
                w="100%"
                minH="200px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={4}
                bg={isDragging ? "#FFF5F8" : "transparent"}
                cursor="pointer"
                transition="all 0.2s"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                {/* Ícone de Pasta */}
                <Image
                    src="/images/icons/UploadDraggerIcon.svg"
                    alt="Upload Dragger Icon"
                    width="48px"
                    height="48px"
                />
                <VStack spacing={1}>
                    <Text
                        fontSize="14px"
                        color="#000"
                        fontWeight="400"
                        textAlign="center"
                    >
                        Clique ou arraste arquivos para essa área para cadastrar
                    </Text>
                    <Text fontSize="12px" color="#8A94A8" textAlign="center">
                        O tamanho máximo permitido é de {maxSize} MB.
                    </Text>
                    <Text fontSize="12px" color="#8A94A8" textAlign="center">
                        Formato aceito {formatAcceptedFormats()}
                    </Text>
                </VStack>

                {selectedFiles.length > 0 && (
                    <VStack spacing={2} mt={4} w="100%" maxW="100%">
                        {selectedFiles.map((file, index) => (
                            <Flex
                                key={index}
                                w="100%"
                                direction="column"
                                p={2}
                                bg="#F9F9F9"
                                borderRadius="4px"
                                gap={1}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Flex
                                    w="100%"
                                    align="center"
                                    justify="space-between"
                                    gap={2}
                                >
                                    <Text
                                        fontSize="12px"
                                        color="#CB2159"
                                        fontWeight="500"
                                        flex={1}
                                        noOfLines={1}
                                        title={file.name}
                                    >
                                        {file.name}
                                    </Text>
                                    <IconButton
                                        aria-label="Remover arquivo"
                                        icon={<FiX />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile(index);
                                        }}
                                    />
                                </Flex>
                                {fileErrors[file.name] && (
                                    <Text
                                        fontSize="11px"
                                        color="#E53E3E"
                                        fontWeight="400"
                                        mt={1}
                                    >
                                        {fileErrors[file.name]}
                                    </Text>
                                )}
                            </Flex>
                        ))}
                    </VStack>
                )}
                
                {/* Exibir erros de arquivos que não foram adicionados */}
                {Object.keys(fileErrors).length > 0 && (
                    <VStack spacing={2} mt={2} w="100%" align="flex-start">
                        {Object.entries(fileErrors)
                            .filter(([filename]) => !selectedFiles.some(f => f.name === filename))
                            .map(([filename, error]) => (
                                <Flex
                                    key={filename}
                                    w="100%"
                                    direction="column"
                                    p={2}
                                    bg="#FED7D7"
                                    borderRadius="4px"
                                    gap={1}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Flex
                                        w="100%"
                                        align="center"
                                        justify="space-between"
                                        gap={2}
                                    >
                                        <Text
                                            fontSize="12px"
                                            color="#C53030"
                                            fontWeight="500"
                                            flex={1}
                                            noOfLines={1}
                                            title={filename}
                                        >
                                            {filename}
                                        </Text>
                                        <IconButton
                                            aria-label="Fechar erro"
                                            icon={<FiX />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newErrors = { ...fileErrors };
                                                delete newErrors[filename];
                                                setFileErrors(newErrors);
                                            }}
                                        />
                                    </Flex>
                                    <Text
                                        fontSize="11px"
                                        color="#C53030"
                                        fontWeight="400"
                                        mt={1}
                                    >
                                        {error}
                                    </Text>
                                </Flex>
                            ))}
                    </VStack>
                )}
            </Box>
            <input
                ref={fileInputRef}
                type="file"
                multiple={multiple}
                accept={acceptedFormats.join(",")}
                onChange={handleFileInputChange}
                style={{ display: "none" }}
            />
        </Flex>
    );
}
